import { Component, ElementRef, forwardRef, OnInit } from '@angular/core';
import { FormBuilderFormBaseComponent } from '../../shared/form-builder-form-base.component';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { RATING_TYPES } from 'src/app/shared/components/ey-rating/ey-display-rating/ey-display-rating.component';

interface DropdownItem {
  name: string;
  value: any;
}

const MAXIMUM_RATING = '10';
const MINIMUM_RATING = '2';

export const META = {
  name: {
    title: 'Name',
  },
  question: {
    title: 'Question',
    required: true,
    errorMsg: 'Question is required. Max length 2000 characters',
  },
  hint: {
    title: 'Hint',
    errorMsg: 'Max length 1000 characters',
  },
  ratingTypes: {
    title: 'Rating Types',
  },
  maxRating: {
    title: 'Max Rating',
  },
};

export const RATING_SELECTION_VALUES: DropdownItem[] = [
  { name: RATING_TYPES.Star, value: 1 },
  { name: RATING_TYPES.Number, value: 2 },
];

@Component({
  standalone: false,
  selector: 'app-rating-editor',
  templateUrl: './rating-editor.component.html',
  styleUrls: ['./rating-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RatingEditorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RatingEditorComponent),
      multi: true,
    },
  ],
})
export class RatingEditorComponent extends FormBuilderFormBaseComponent implements OnInit {
  meta = META;
  defRatingType = RATING_SELECTION_VALUES[0];
  ratingTypes: DropdownItem[] = RATING_SELECTION_VALUES;
  form = this.fbs.group(
    {
      id: [''],
      name: ['', Validators.required],
      question: ['Your Question', [Validators.required, Validators.maxLength(2000)]],
      hint: [, Validators.maxLength(1000)],
      required: [false, { updateOn: 'change' }],
      ratingType: [this.defRatingType, Validators.required],
      maxRating: [5, [(Validators.required, Validators.min(0), Validators.max(10))]],
    },
    { updateOn: 'blur' },
  );

  get ratingTypeCtrl(): UntypedFormControl {
    return this.form.get('ratingType') as UntypedFormControl;
  }

  get maxRatingControl(): UntypedFormControl {
    return this.form.controls.maxRating as UntypedFormControl;
  }

  constructor(
    private fbs: UntypedFormBuilder,
    private els: ElementRef,
  ) {
    super(fbs, els);
  }

  ngOnInit(): void {
    this.subscribeToFormChanges();
  }

  subscribeToFormChanges(): void {
    this.maxRatingControl.valueChanges.subscribe((v) => {
      const rating = +v;

      if (rating > +MAXIMUM_RATING) {
        this.maxRatingControl.setValue(MAXIMUM_RATING, { emitEvent: false });
      }

      if (rating < +MINIMUM_RATING) {
        this.maxRatingControl.setValue(MINIMUM_RATING, { emitEvent: false });
      }
    });

    this.form.valueChanges.subscribe((v) => {
      if (this.form.valid && (!v.required || (v.required && !this.requiredFieldCrossValidationError))) {
        this.onChange(this.convertToReturnValue(v));
      }
    });

    this.form.statusChanges.subscribe(this.onValidatorChange);

    this.onChange(this.convertToReturnValue(this.form.value));
  }

  convertToReturnValue(value: any): any {
    return {
      ...value,
      ratingType: value?.ratingType?.value,
    };
  }

  validate(c: AbstractControl): ValidationErrors | null {
    return this.form.valid ? null : { subformerror: 'Rating Editor Form Error!' };
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  onChange: (val: any) => void = () => {};
  onValidatorChange: () => void = () => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  writeValue(val: any): void {
    if (val) {
      const patchValue = val.ratingType
        ? {
            ...val,
            ratingType: this.ratingTypes.find((c) => c.value === val.ratingType),
          }
        : val;

      this.form.patchValue(patchValue);
    }
  }
}
